import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Physician } from 'app/core/models/physician.model';
import { UserAddress } from 'app/core/models/user.model';
import { PaginatorState } from 'primeng/paginator';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { SearchNearPhysiciansByAddressRequestDTO } from '../../dtos/search-near-physicians-by-adress.dto';
import { CouponApiService } from '../../services/coupon-api.service';
import { CouponFormsService } from '../../services/coupon-forms.service';
import { AddressUtil } from 'app/core/utils/address.utils';
import { AppointmentCandidateStatus } from 'app/core/models/appointment-candidate.model';

interface PhysicianSearchResult {
  loading: boolean;
  physicians: Physician[];
  totalRecords: number;
  error?: unknown;
}

const InitialPhysicianSearchResult: PhysicianSearchResult = {
  loading: false,
  physicians: [],
  totalRecords: 0,
};

@Component({
  selector: 'app-physician-list',
  templateUrl: './physician-list.component.html',
  styleUrls: ['./physician-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhysicianListComponent implements OnInit {
  page = signal(0);
  rows = signal(10);
  userAddresses: UserAddress[] = [];
  private router = inject(Router);
  public formsService = inject(CouponFormsService);
  private couponApiService = inject(CouponApiService);
  private searchTrigger$ = new BehaviorSubject<void>(undefined);
  physicianSearchResult$!: Observable<PhysicianSearchResult>;
  physiciansCandidates = signal<PhysicianSearchResult>(
    InitialPhysicianSearchResult,
  );

  ngOnInit() {
    this.physicianSearchResult$ = this.searchTrigger$.pipe(
      switchMap(() =>
        combineLatest([
          this.fetchPhysicians(),
          this.listPhysiciansCandidates(),
        ]).pipe(
          map(([searchResult, candidatesResult]) => {
            const candidatesPhysicianIds = new Set(
              (candidatesResult.physicians || []).map(
                physician => physician.physicianId,
              ),
            );

            const filteredPhysicians = (searchResult.physicians || []).filter(
              physician => !candidatesPhysicianIds.has(physician.physicianId),
            );

            return {
              loading: searchResult.loading,
              physicians: filteredPhysicians,
              totalRecords: searchResult.totalRecords,
            };
          }),
        ),
      ),
    );

    this.updateUserAddresses();
  }

  loadPhysicians() {
    this.searchTrigger$.next();
  }

  private listPhysiciansCandidates(): Observable<PhysicianSearchResult> {
    const addr = this.formsService.formAddress.value;

    return this.couponApiService
      .listPhysiciansCandidatesByUserId({
        customerId: this.formsService.formUser.value.id!,
        latitude: Number(addr.latitude) || undefined,
        longitude: Number(addr.longitude) || undefined,
        status: [AppointmentCandidateStatus.open],
      })
      .pipe(
        map(({ response }) => ({
          loading: false,
          physicians: response.sort((a, b) => {
            if (a.distanceInKilometers && b.distanceInKilometers) {
              return a.distanceInKilometers - b.distanceInKilometers > 0
                ? 1
                : -1;
            }
            return 0;
          }),
          totalRecords: response.length,
        })),
        startWith({ ...InitialPhysicianSearchResult, loading: true }),
        catchError(error => {
          console.error('Erro ao buscar dentistas de interesse:', error);
          return of(InitialPhysicianSearchResult);
        }),
        tap(result => {
          this.physiciansCandidates.set({ ...result, loading: false });
        }),
      );
  }

  private fetchPhysicians(): Observable<PhysicianSearchResult> {
    const addr = this.formsService.formAddress.value;
    const currentPage = this.page() + 1;
    const params: SearchNearPhysiciansByAddressRequestDTO = {
      zipCode: addr.zipCode || '',
      state: addr.state || '',
      city: addr.city || '',
      neighborhood: addr.neighborhood || '',
      street: addr.street || '',
      number: addr.number || '',
      complement: addr.complement || '',
      reference: addr.reference || '',
      latitude: (addr.latitude && Number(addr.latitude)) || undefined,
      longitude: (addr.longitude && Number(addr.longitude)) || undefined,
      page: currentPage,
      pageSize: this.rows(),
    };

    return this.couponApiService.searchNearPhysiciansByAddress(params).pipe(
      map(({ response }) => {
        const physiciansPage = response;
        return {
          loading: false,
          physicians:
            physiciansPage.items || InitialPhysicianSearchResult.physicians,
          totalRecords:
            physiciansPage.totalItems ||
            InitialPhysicianSearchResult.totalRecords,
        };
      }),
      startWith({ ...InitialPhysicianSearchResult, loading: true }),
      catchError(error => {
        console.error('Erro ao buscar dentistas:', error);
        return of({
          loading: false,
          physicians: [],
          totalRecords: 0,
          error,
        });
      }),
    );
  }

  getCompleteAddress(): string {
    const address = this.formsService.formAddress.value;
    if (!address.street) return '';
    return `${address.street}, ${address.number || ''} - ${address.city || ''}/${address.state || ''}`;
  }

  getFormattedAddress(address: UserAddress): string {
    return AddressUtil.getFormattedAddress(address);
  }

  getDoctorCompleteAddress(doctor: Physician): string {
    return [
      doctor.addressStreet,
      doctor.addressNumber,
      doctor.addressNeighborhood,
      doctor.addressCity,
      doctor.addressState,
    ]
      .filter(Boolean)
      .join(', ');
  }

  onPageChange(event: PaginatorState) {
    if (event.page !== undefined) this.page.set(event.page);
    if (event.rows !== undefined) this.rows.set(event.rows);
    this.loadPhysicians();
  }

  selectDoctor(doctor: Physician, hasCandidates?: boolean) {
    this.formsService.formPhysician.patchValue({
      physicianId: doctor.physicianId,
      physicianName: doctor.physicianName,
      physicianCpf: doctor.physicianCpf,
      physicianBirthDate: doctor.physicianBirthDate,
      physicianCro: doctor.physicianCro,
      physicianPhone: doctor.physicianPhone,
      physicianEmail: doctor.physicianEmail,
      physicianBranchCode: doctor.physicianBranchCode,
      physicianBranchName: doctor.physicianBranchName,
      addressStreet: doctor.addressStreet,
      addressNumber: doctor.addressNumber,
      addressNeighborhood: doctor.addressNeighborhood,
      addressCity: doctor.addressCity,
      addressState: doctor.addressState,
      addressZipCode: doctor.addressZipCode,
      longitude: doctor.longitude,
      latitude: doctor.latitude,
      distanceInKilometers: doctor.distanceInKilometers,
      score: doctor.score,
      accredited: doctor.accredited,
      hasCandidates,
    });
  }

  selectAddress(address: UserAddress) {
    this.formsService.formAddress.patchValue({
      id: address.id,
      zipCode: address.zipCode,
      state: address.state,
      city: address.city,
      neighborhood: address.neighborhood,
      street: address.street,
      number: address.number,
      complement: address.complement,
      reference: address.reference,
      latitude: String(address.latitude),
      longitude: String(address.longitude),
      selectedAddress: address,
    });

    this.updateUserAddresses();
    this.page.set(0);
    this.loadPhysicians();
  }

  isSelectedAddress(addressId: string): boolean {
    return (
      this.formsService.formAddress.value.selectedAddress?.id === addressId
    );
  }

  isSelected(doctor: Physician): boolean {
    const current = this.formsService.formPhysician.value;
    return current.physicianId === doctor.physicianId;
  }

  goBack() {
    this.router.navigate(['/coupon']);
  }

  onGoNext() {
    this.router.navigate(['/coupon/physician-details']);
  }

  private updateUserAddresses() {
    this.userAddresses = this.formsService.getUserAddressesAsArray();
  }
}
